/**
 * Edit Goal Drawer Component
 * แก้ไขเป้าหมายปัจจุบันผ่าน Drawer
 * ใช้ API และ form logic เหมือน SetGoalForm แต่เป็นการ update
 */

import { useState, useMemo, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Minus, Plus, CalendarRange, Info, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { CustomCalendar } from '@/components/ui/custom-calendar';
import { goalsService } from '@/services/goals.service';
import { cn } from '@/lib/utils';
import {
  totalPagesForGoal,
  type Goal,
  type UpdateGoalRequest,
} from '@/types/goals';

const DEFAULT_TIMEZONE = 'Asia/Bangkok';
const MIN_KHATM = 1;
const MAX_KHATM = 100;

function getDaysBetween(start: string, end: string): number {
  const a = new Date(start + 'T00:00:00').getTime();
  const b = new Date(end + 'T23:59:59').getTime();
  return Math.max(1, Math.ceil((b - a) / (24 * 60 * 60 * 1000)));
}

function dateToYYYYMMDD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface EditGoalDrawerProps {
  open: boolean;
  onClose: () => void;
  goal: Goal | null;
}

export function EditGoalDrawer({ open, onClose, goal }: EditGoalDrawerProps) {
  const queryClient = useQueryClient();

  const [khatmCount, setKhatmCount] = useState(goal?.khatm_count || 1);
  const [endDate, setEndDate] = useState(goal?.end_date || '');
  const [startDate, setStartDate] = useState(goal?.start_date || '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Reset form when goal changes or drawer opens
  useEffect(() => {
    if (goal && open) {
      setKhatmCount(goal.khatm_count);
      setEndDate(goal.end_date);
      setStartDate(goal.start_date);
      setFieldErrors({});
    }
  }, [goal, open]);

  const totalPages = totalPagesForGoal(khatmCount);
  const durationDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return getDaysBetween(startDate, endDate);
  }, [startDate, endDate]);
  const pagesPerDay = durationDays > 0 ? Math.ceil(totalPages / durationDays) : 0;

  const updateMutation = useMutation({
    mutationFn: (body: UpdateGoalRequest) => {
      if (!goal) throw new Error('No goal to update');
      return goalsService.updateGoal(goal.id, body);
    },
    onSuccess: (res) => {
      queryClient.setQueryData(['goals', 'current'], res.data);
      queryClient.invalidateQueries({ queryKey: ['goals', 'current', 'progress'] });
      toast.success('อัปเดตเป้าหมายสำเร็จ', { position: 'top-center' });
      if (res.warnings?.length) {
        res.warnings.forEach((w) => toast(w, { icon: '⚠️' }));
      }
      onClose();
    },
    onError: (err: Error & { details?: { path: string; message: string }[] }) => {
      const details = err.details;
      if (details?.length) {
        const next: Record<string, string> = {};
        details.forEach((d) => {
          next[d.path] = d.message;
        });
        setFieldErrors(next);
        toast.error(details.map((d) => d.message).join(', '));
      } else {
        setFieldErrors({ form: err.message });
        toast.error(err.message || 'เกิดข้อผิดพลาด');
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    
    if (khatmCount < MIN_KHATM || khatmCount > MAX_KHATM) {
      setFieldErrors({
        khatm_count: `กรุณาระบุจำนวน Khatm ระหว่าง ${MIN_KHATM}–${MAX_KHATM}`,
      });
      toast.error(`กรุณาระบุจำนวน Khatm ระหว่าง ${MIN_KHATM}–${MAX_KHATM}`);
      return;
    }
    if (!endDate.trim()) {
      setFieldErrors({ end_date: 'กรุณาระบุวันสิ้นสุด' });
      toast.error('กรุณาระบุวันสิ้นสุด');
      return;
    }
    
    const body: UpdateGoalRequest = {
      khatm_count: khatmCount,
      end_date: endDate.trim(),
    };
    if (startDate.trim()) body.start_date = startDate.trim();
    body.timezone = DEFAULT_TIMEZONE;
    
    updateMutation.mutate(body);
  };

  const decrement = () => setKhatmCount((c) => Math.max(MIN_KHATM, c - 1));
  const increment = () => setKhatmCount((c) => Math.min(MAX_KHATM, c + 1));

  if (!goal) return null;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            แก้ไขเป้าหมาย
          </SheetTitle>
          <SheetDescription>
            ปรับเป้าหมายให้เหมาะกับความต้องการของคุณ
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          {/* เป้าหมาย (Khatm) */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="khatm-edit" className="text-foreground">
                เป้าหมาย (Khatm)
              </Label>
              <span
                className="text-muted-foreground cursor-help"
                title="1 Khatm = อ่านกุรอานครบ 604 หน้า (หนึ่งเล่ม)"
                aria-label="ข้อมูล"
              >
                <Info className="h-4 w-4" />
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="rounded-full h-10 w-10 shrink-0 border-primary text-primary bg-primary/10 hover:bg-primary/20"
                onClick={decrement}
                aria-label="ลดจำนวน"
              >
                <Minus className="h-5 w-5" />
              </Button>
              <Input
                id="khatm-edit"
                type="number"
                min={MIN_KHATM}
                max={MAX_KHATM}
                value={khatmCount}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!Number.isNaN(v))
                    setKhatmCount(Math.min(MAX_KHATM, Math.max(MIN_KHATM, v)));
                }}
                className={cn(
                  'flex-1 h-12 text-center text-lg font-semibold rounded-lg border-primary bg-primary/10',
                  fieldErrors.khatm_count && 'border-destructive'
                )}
                aria-invalid={!!fieldErrors.khatm_count}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="rounded-full h-10 w-10 shrink-0 border-primary text-primary bg-primary/10 hover:bg-primary/20"
                onClick={increment}
                aria-label="เพิ่มจำนวน"
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              {khatmCount.toLocaleString('th-TH')} Khatm = {totalPagesForGoal(khatmCount).toLocaleString('th-TH')} หน้า
            </p>
            {fieldErrors.khatm_count && (
              <p className="text-sm text-destructive" role="alert">
                {fieldErrors.khatm_count}
              </p>
            )}
          </div>

          {/* ช่วงวันเป้าหมาย */}
          <div className="space-y-2">
            <Label className="text-foreground flex items-center gap-2">
              <CalendarRange className="h-4 w-4" />
              ช่วงวันเป้าหมาย
            </Label>
            <CustomCalendar
              disablePastDates={false}
              defaultMonth={startDate ? new Date(startDate + 'T00:00:00') : new Date()}
              selected={
                startDate && endDate
                  ? {
                      from: new Date(startDate + 'T00:00:00'),
                      to: new Date(endDate + 'T00:00:00'),
                    }
                  : undefined
              }
              onSelect={(range) => {
                if (range?.from) {
                  setStartDate(dateToYYYYMMDD(range.from));
                  setEndDate(range?.to ? dateToYYYYMMDD(range.to) : dateToYYYYMMDD(range.from));
                }
              }}
              className={cn(
                'w-full',
                fieldErrors.end_date && 'border-destructive'
              )}
              showFooter={true}
            />
            {fieldErrors.end_date && (
              <p className="text-sm text-destructive" role="alert">
                {fieldErrors.end_date}
              </p>
            )}
          </div>

          {/* การ์ดคำนวณอัตโนมัติ */}
          <Card className="rounded-xl border-l-4 border-l-primary bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-card-foreground">
                <Info className="h-4 w-4 text-muted-foreground" />
                คำนวณอัตโนมัติ
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 pt-0">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">ระยะเวลา</span>
                <span className="text-card-foreground font-medium">
                  {durationDays.toLocaleString('th-TH')} วัน
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">หน้าทั้งหมด</span>
                <span className="text-card-foreground font-medium">
                  {totalPages.toLocaleString('th-TH')} หน้า
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">หน้าต่อวัน</span>
                <span className="text-primary font-semibold">
                  {pagesPerDay.toLocaleString('th-TH')} หน้า
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={updateMutation.isPending}
            >
              ยกเลิก
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex-1"
            >
              {updateMutation.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export default EditGoalDrawer;
